import React, { useRef, useEffect, useLayoutEffect, useCallback, useState, useMemo } from 'react';
import {
  X,
  Search,
  Wine,
  Tag,
  Mic,
  MicOff,
  SlidersHorizontal,
} from 'lucide-react';
import { CATEGORY_OPTIONS } from '../utils/types';
import type { ItemsPerRow, FacetBucket } from '../utils/types';
import { getCatalogFacets } from '../utils/api';
import { useSearch } from '../hooks/useSearch';
import { useVoiceInput } from '../hooks/useVoiceInput';
import { useLayoutConfig } from '../hooks/useLayoutConfig';
import { useSearchHistory, readInitialRestore } from '../hooks/useSearchHistory';
import { useI18n } from '../i18n/I18nContext';
import { translateFacetLabel } from '../i18n/index';
import { FacetSidebar } from './FacetSidebar';
import { FilterChips, type FilterChip } from './FilterChips';
import { SkeletonGrid } from './SkeletonCard';
import { SortDropdown, type SortOption } from './SortDropdown';
import { LayoutRenderer } from './LayoutRenderer';
import { ResultsRenderer } from './ResultsRenderer';
import { WidgetPortal } from './WidgetPortal';
import { BestSellerWinesWidget } from './widgets/BestSellerWinesWidget';
import { FontSizeProvider } from '../contexts/FontSizeContext';

interface SearchWidgetProps {
  /** Override the API base URL (used by the embeddable widget) */
  apiUrl?: string;
  /** Language partition for search and UI labels */
  lang?: string;
  /** Headless mode: hide the trigger input (used when an external element opens search) */
  headless?: boolean;
  /** Called once on mount to expose open/close API to the parent */
  onReady?: (api: { open: () => void; close: () => void }) => void;
  /** When provided, renders a Greek/English switcher in the modal header */
  langSwitcher?: { lang: string; setLang: (lang: string) => void };
}

/* ------------------------------------------------------------------ */
/*  Critical inline styles — ensure the modal is visible and properly  */
/*  sized even when the external CSS (widget.css) hasn't loaded yet.   */
/*  These duplicate some Tailwind utilities on purpose.                */
/* ------------------------------------------------------------------ */

const WIDGET_ROOT_STYLE: React.CSSProperties = {
  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, sans-serif",
  fontSize: '16px',
  lineHeight: '1.5',
  color: '#1a1a1a',
  WebkitFontSmoothing: 'antialiased',
  MozOsxFontSmoothing: 'grayscale',
  boxSizing: 'border-box',
};

const FULLSCREEN_STYLE: React.CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  // NOTE: do NOT set width:100vw / height:100vh here. `inset:0` already sizes
  // the element to the viewport *content* box; 100vw/100vh include the
  // scrollbar, making the modal wider/taller than the visible area → horizontal
  // overflow and the modal "not covering the full screen".
  zIndex: 10000,
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: '#fff',
  // Safety net: clip any stray horizontal overflow from a child so a single
  // mis-sized element can never push a viewport-level horizontal scrollbar that
  // makes the fixed modal appear not to cover the full screen. Inline (not a
  // class) so host-page CSS in the embed can't override it.
  overflowX: 'hidden',
  ...WIDGET_ROOT_STYLE,
};

const HEADER_STYLE: React.CSSProperties = {
  flexShrink: 0,
  backgroundColor: '#fff',
  borderBottom: '1px solid #1a1a1a',
  zIndex: 20,
};

const INPUT_STYLE: React.CSSProperties = {
  width: '100%',
  paddingLeft: '48px',
  paddingRight: '48px',
  paddingTop: '14px',
  paddingBottom: '14px',
  fontSize: '18px',
  fontFamily: 'inherit',
  backgroundColor: '#fff',
  border: '1px solid #1a1a1a',
  borderRadius: '2px',
  outline: 'none',
  boxSizing: 'border-box',
  color: '#1a1a1a',
};

const MAIN_SCROLL_STYLE: React.CSSProperties = {
  flex: 1,
  overflowY: 'auto',
};

export const SearchWidget: React.FC<SearchWidgetProps> = ({ lang, headless = false, onReady, langSwitcher }) => {
  const { t } = useI18n();
  // Back-button return: decided synchronously so the very first render is the
  // open widget with the previous results (no host-page / empty frame first).
  const [initialRestore] = useState(() => readInitialRestore());
  const {
    query,
    results,
    defaults,
    autocomplete,
    isLoading,
    isLoadingMore,
    isLoadingDefaults,
    error,
    activeIndex,
    recentSearches,
    recentProducts,
    filters,
    showAutocomplete,
    hasMore,
    setQuery,
    setFilters,
    clearFilters,
    applyBrowseFilters,
    open,
    close,
    selectSuggestion,
    clearQuery,
    clearRecentSearches,
    trackProductClick,
    handleKeyDown,
    toggleSidebar,
    loadMore,
    searchExact,
    isAIThinking,
    restoreSearchState,
  } = useSearch(lang, initialRestore);

  const { layoutConfig, isLoading: isLayoutLoading } = useLayoutConfig(lang);

  const { isListening, isSupported, error: voiceError, startListening, stopListening } = useVoiceInput({
    lang: lang === 'en' ? 'en-US' : 'el-GR',
    onInterimResult: setQuery,
    onFinalResult: setQuery,
  });

  const [isOpen, setIsOpen] = useState(!!initialRestore);
  // Opened from a link or Back: no fade, the host page must not show through.
  const [noFade, setNoFade] = useState(!!initialRestore);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 640);
  // Compact layout = phone/tablet (mirrors the `lg` breakpoint). Drives the
  // filters layout (inline sidebar vs. drawer) via conditional RENDERING rather
  // than CSS `display:none`, because host-page CSS can override the latter in the
  // embedded widget (the sidebar was showing on mobile despite the media query).
  const [isCompact, setIsCompact] = useState(() => typeof window !== 'undefined' && window.innerWidth < 1024);
  const triggerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const modalInputRef = useRef<HTMLInputElement>(null);

  // Track viewport width for responsive inline styles
  useEffect(() => {
    const mqMobile = window.matchMedia('(max-width: 639px)');
    const mqCompact = window.matchMedia('(max-width: 1023px)');
    const onMobile = (e: MediaQueryListEvent | MediaQueryList) => setIsMobile(e.matches);
    const onCompact = (e: MediaQueryListEvent | MediaQueryList) => setIsCompact(e.matches);
    onMobile(mqMobile);
    onCompact(mqCompact);
    mqMobile.addEventListener('change', onMobile as (e: MediaQueryListEvent) => void);
    mqCompact.addEventListener('change', onCompact as (e: MediaQueryListEvent) => void);
    return () => {
      mqMobile.removeEventListener('change', onMobile as (e: MediaQueryListEvent) => void);
      mqCompact.removeEventListener('change', onCompact as (e: MediaQueryListEvent) => void);
    };
  }, []);

  // -------------------------------------------------------------------------
  // Open / close
  // -------------------------------------------------------------------------
  // openUi/closeUi only touch the UI. openWidget/closeWidget (below) are the
  // user-facing versions that also push/pop the browser-history entry.

  const openUi = useCallback((focusInput = true) => {
    setIsOpen(true);
    open();
    if (layoutConfig.modalMode === 'fullscreen') {
      document.body.style.overflow = 'hidden';
      // Fix rem scaling: host pages may set html { font-size: 10px } (or other
      // non-16px values) which shrinks every rem-based Tailwind utility.
      // While the fullscreen modal is open the host page is hidden, so we can
      // safely override the root font-size to the browser default (16px).
      const html = document.documentElement;
      if (!html.dataset.botiliaOrigFontSize) {
        html.dataset.botiliaOrigFontSize = html.style.fontSize || '';
      }
      html.style.fontSize = '16px';
    }
    if (focusInput) {
      setTimeout(() => {
        modalInputRef.current?.focus();
      }, 100);
    }
  }, [open, layoutConfig.modalMode]);

  const closeUi = useCallback(() => {
    setIsOpen(false);
    close();
    stopListening();
    document.body.style.overflow = '';
    // Restore the host page's original html font-size
    const html = document.documentElement;
    if (html.dataset.botiliaOrigFontSize !== undefined) {
      html.style.fontSize = html.dataset.botiliaOrigFontSize;
      delete html.dataset.botiliaOrigFontSize;
    }
  }, [close, stopListening]);

  // Browser history integration: Back closes the widget, Back from a product
  // page (or Forward) reopens it with the same query/filters/scroll, and the
  // query is mirrored into ?bsq= so the state survives a reload.
  // A restore can fire at mount, before /api/layout has answered; the search
  // starts immediately but the modal is shown only once the mode (fullscreen
  // vs dropdown) is known, so openUi applies the right body/html side effects.
  const pendingOpenRef = useRef(false);
  const isLayoutLoadingRef = useRef(isLayoutLoading);
  isLayoutLoadingRef.current = isLayoutLoading;
  const { notifyOpen, notifyClose, scrollContainerRef, applyPendingScroll } = useSearchHistory({
    isOpen,
    query,
    filters,
    loadedCount: results?.products.length ?? 0,
    initialRestore,
    onRestore: (state) => {
      setNoFade(true);
      restoreSearchState(state);
      if (isLayoutLoadingRef.current) {
        pendingOpenRef.current = true;
      } else {
        // Don't steal focus (and pop the mobile keyboard) when returning to results.
        openUi(false);
      }
    },
    onExternalClose: closeUi,
  });
  useEffect(() => {
    if (isLayoutLoading || !pendingOpenRef.current) return;
    pendingOpenRef.current = false;
    openUi(false);
  }, [isLayoutLoading, openUi]);

  // The host page may have covered itself while the widget loaded (data-bs-restoring,
  // set by an inline script in its <head>). The widget is on screen now.
  useEffect(() => {
    document.documentElement.removeAttribute('data-bs-restoring');
  }, []);

  // Mount-time restore: the UI is already rendered open (isOpen seeded above);
  // this only applies the mode side effects (body scroll lock, root font-size).
  useEffect(() => {
    if (!initialRestore) return;
    if (isLayoutLoadingRef.current) pendingOpenRef.current = true;
    else openUi(false);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openWidget = useCallback(() => {
    setNoFade(false);
    openUi();
    notifyOpen();
  }, [openUi, notifyOpen]);

  const closeWidget = useCallback(() => {
    closeUi();
    notifyClose();
  }, [closeUi, notifyClose]);

  // Restore the saved scroll position in the same frame the results commit
  // (layout effect = before paint), so there is no visible jump from the top.
  useLayoutEffect(() => {
    if (!isOpen || !results) return;
    applyPendingScroll();
  }, [isOpen, isLoading, results, applyPendingScroll]);

  // Escape key closes
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) closeWidget();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, closeWidget]);

  // Expose open/close API to parent (for trigger mode in widget.tsx)
  const onReadyRef = useRef(onReady);
  onReadyRef.current = onReady;
  useEffect(() => {
    onReadyRef.current?.({ open: openWidget, close: closeWidget });
  }, [openWidget, closeWidget]);

  // Click-outside closes dropdown mode.
  // Use composedPath() rather than e.target: when the widget is mounted in a
  // Shadow DOM the event target is retargeted to the shadow host by the time
  // it reaches `document`, so e.target would never match inner nodes.
  useEffect(() => {
    if (layoutConfig.modalMode !== 'dropdown' || !isOpen) return;
    const handler = (e: MouseEvent) => {
      const path = e.composedPath();
      const insideTrigger = !!triggerRef.current && path.includes(triggerRef.current);
      const insidePanel = path.some(
        (n) => n instanceof Element && n.hasAttribute('data-search-panel'),
      );
      if (!insideTrigger && !insidePanel) {
        closeWidget();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [layoutConfig.modalMode, isOpen, closeWidget]);

  // -------------------------------------------------------------------------
  // Filter chips
  // -------------------------------------------------------------------------

  const filterChips = useMemo((): FilterChip[] => {
    const chips: FilterChip[] = [];
    const tl = (v: string | undefined) => v ? translateFacetLabel(lang ?? 'el', v) : '';
    if (filters.category) chips.push({ id: 'category', type: 'category', label: `${t('filter.category')}: ${tl(filters.category)}`, value: filters.category });
    if (filters.type) chips.push({ id: 'type', type: 'type', label: `${t('filter.type')}: ${tl(filters.type)}`, value: filters.type });
    if (filters.country) chips.push({ id: 'country', type: 'country', label: `${t('filter.country')}: ${tl(filters.country)}`, value: filters.country });
    if (filters.region) chips.push({ id: 'region', type: 'region', label: `${t('filter.region')}: ${tl(filters.region)}`, value: filters.region });
    if (filters.grape) chips.push({ id: 'grape', type: 'grape', label: `${t('filter.grape')}: ${tl(filters.grape)}`, value: filters.grape });
    if (filters.producer) chips.push({ id: 'producer', type: 'producer', label: `${t('filter.producer')}: ${tl(filters.producer)}`, value: filters.producer });
    if (filters.minPrice !== undefined || filters.maxPrice !== undefined) {
      const priceLabel = filters.minPrice !== undefined && filters.maxPrice !== undefined
        ? `€${filters.minPrice} - €${filters.maxPrice}`
        : filters.minPrice !== undefined ? `${t('filter.priceFrom')} €${filters.minPrice}` : `${t('filter.priceTo')} €${filters.maxPrice}`;
      chips.push({ id: 'price', type: 'price', label: priceLabel, value: priceLabel });
    }
    if (filters.inStock) chips.push({ id: 'stock', type: 'stock', label: t('filter.inStock'), value: 'true' });
    return chips;
  }, [filters, t]);

  const handleRemoveChip = useCallback((chipId: string) => {
    const newFilters = { ...filters };
    if (chipId === 'category') delete newFilters.category;
    else if (chipId === 'type') delete newFilters.type;
    else if (chipId === 'country') delete newFilters.country;
    else if (chipId === 'region') delete newFilters.region;
    else if (chipId === 'grape') delete newFilters.grape;
    else if (chipId === 'producer') delete newFilters.producer;
    else if (chipId === 'price') { delete newFilters.minPrice; delete newFilters.maxPrice; }
    else if (chipId === 'stock') delete newFilters.inStock;
    setFilters(newFilters);
  }, [filters, setFilters]);

  const handleSortChange = useCallback((sort: SortOption) => {
    setFilters({ ...filters, sort: sort === 'relevance' ? undefined : sort });
  }, [filters, setFilters]);

  // -------------------------------------------------------------------------
  // Dropdown mode positioning
  // -------------------------------------------------------------------------

  const dropdownStyle = useMemo(() => {
    if (layoutConfig.modalMode !== 'dropdown' || !triggerRef.current) return {};
    const rect = triggerRef.current.getBoundingClientRect();
    return {
      top: rect.bottom + 8,
      left: rect.left,
      width: Math.max(rect.width, Math.min(640, window.innerWidth * 0.96)),
      // px (not vw/vh) so the scrollbar gutter and the iOS dynamic toolbar are
      // excluded — vw/vh include them and would overflow the visible area.
      maxWidth: Math.min(640, window.innerWidth * 0.96),
      maxHeight: Math.max(240, window.innerHeight - (rect.bottom + 8) - 16),
    };
  }, [layoutConfig.modalMode, isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // -------------------------------------------------------------------------
  // View state
  // -------------------------------------------------------------------------

  const hasActiveFilters = Object.values(filters).some((v) => v !== undefined);
  const showDiscoveryView = isOpen && query.trim().length === 0 && !hasActiveFilters && !isLoading;
  const showSearchResults = isOpen && (query.trim().length > 0 || hasActiveFilters);

  // -------------------------------------------------------------------------
  // Shared widget data
  // -------------------------------------------------------------------------

  const activeItemsPerRow = layoutConfig.modalMode === 'fullscreen'
    ? (layoutConfig.fullscreenItemsPerRow ?? 4)
    : (layoutConfig.dropdownItemsPerRow ?? 3);

  const widgetBaseProps = {
    defaults,
    results,
    recentSearches,
    recentProducts,
    onSearch: selectSuggestion,
    onClearRecentSearches: clearRecentSearches,
    onProductClick: trackProductClick,
    itemsPerRow: activeItemsPerRow as ItemsPerRow,
    onApplyFilters: applyBrowseFilters,
  };


  // -------------------------------------------------------------------------
  // Category quick jump matching
  // -------------------------------------------------------------------------

  // Real catalog categories (Εξάδες, Ποτά, …) so the quick-jump isn't limited
  // to the hardcoded wine types — searching a category's name surfaces the
  // category itself as a chip.
  const [catalogCategories, setCatalogCategories] = useState<FacetBucket[]>([]);
  useEffect(() => {
    getCatalogFacets(lang).then((facets) => {
      if (facets?.categories && Array.isArray(facets.categories)) {
        setCatalogCategories(facets.categories as FacetBucket[]);
      }
    });
  }, [lang]);

  const matchedCategories = useMemo(() => {
    const fold = (v: string) =>
      v.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    const q = fold(query.trim());
    if (!q) return [];

    type QuickJump = { key: string; label: string; filters: { type?: string; category?: string } };
    const out: QuickJump[] = [];
    const seen = new Set<string>();

    // Wine-type chips (existing behavior)
    for (const cat of CATEGORY_OPTIONS) {
      if (cat.value === '') continue;
      if (fold(cat.label).includes(q) || fold(cat.labelEn).includes(q)) {
        out.push({ key: `type:${cat.value}`, label: cat.label, filters: { type: cat.value } });
        seen.add(fold(cat.label));
      }
    }

    // Catalog category chips (need ≥3 chars so single letters don't match everything)
    if (q.length >= 3) {
      for (const cat of catalogCategories) {
        const label = cat.label || cat.value;
        const folded = fold(label);
        if (seen.has(folded) || !folded.includes(q)) continue;
        out.push({ key: `category:${cat.value}`, label, filters: { category: cat.value } });
        seen.add(folded);
      }
    }

    return out.slice(0, 4);
  }, [query, catalogCategories]);

  // -------------------------------------------------------------------------
  // Header row content (shown inside the sticky header below the search input)
  // -------------------------------------------------------------------------

  const headerContent = (
    <>
      {/* Autocomplete suggestions — single scrollable row (edge-to-edge on mobile) */}
      {showAutocomplete && autocomplete.length > 0 && (
        <div className="mt-2 flex flex-nowrap gap-1.5 sm:gap-2 overflow-x-auto pb-1.5 -mx-3 px-3 sm:mx-0 sm:px-0 botilia-scrollbar animate-fade-in">
          {autocomplete.slice(0, 7).map((suggestion, index) => (
            <button
              key={suggestion}
              type="button"
              onClick={() => selectSuggestion(suggestion)}
              className={[
                'flex-shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1 sm:px-4 sm:py-2 rounded-full text-xs sm:text-sm whitespace-nowrap transition-all',
                activeIndex === index
                  ? 'bg-brand text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-brand/10 hover:text-brand',
              ].join(' ')}
            >
              <Search className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
              {suggestion}
            </button>
          ))}
        </div>
      )}

      {/* Category quick jumps */}
      {isOpen && query.trim().length > 0 && matchedCategories.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 mt-2 animate-fade-in">
          <span className="text-xs text-gray-400">{t('search.category')}</span>
          {matchedCategories.map((cat) => (
            <button
              key={cat.key}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                clearFilters();
                setFilters(cat.filters);
                clearQuery();
              }}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-brand/10 text-brand-ink hover:bg-brand/20 transition-colors focus:outline-none focus:ring-2 focus:ring-brand/20"
            >
              <Tag className="h-3 w-3" />
              {cat.label}
            </button>
          ))}
        </div>
      )}

      {/* Header layout widgets (shown when no autocomplete) */}
      {!showAutocomplete && showDiscoveryView && (
        <LayoutRenderer
          zone="header"
          layoutConfig={layoutConfig}
          {...widgetBaseProps}
        />
      )}
    </>
  );

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  const isFullscreen = layoutConfig.modalMode === 'fullscreen';

  return (
    <FontSizeProvider config={layoutConfig.fontSizes}>
      {/* Trigger input (hidden in headless/trigger mode) */}
      {!headless && (
        <div
          ref={triggerRef}
          className="botilia-search relative w-full"
          role="search"
          aria-label={t('search.aria')}
        >
          <div className="relative cursor-pointer group" onClick={openWidget}>
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 group-hover:text-brand transition-colors" />
            <input
              ref={inputRef}
              type="text"
              placeholder={t('search.placeholder')}
              className="w-full pl-12 pr-4 py-3.5 text-base bg-white border border-[#1a1a1a] rounded-[2px] cursor-pointer focus:outline focus:outline-2 focus:outline-brand"
              onFocus={openWidget}
              readOnly
              aria-label={t('search.clickToSearch')}
            />
            <div className="absolute right-4 top-1/2 -translate-y-1/2 botilia-show-sm-flex items-center gap-1 text-xs text-gray-400">
              <kbd className="px-1.5 py-0.5 bg-gray-100 rounded text-[10px] font-mono">/</kbd>
            </div>
          </div>
        </div>
      )}

      {/* ---------------------------------------------------------------- */}
      {/* Fullscreen modal                                                  */}
      {/* ---------------------------------------------------------------- */}
      {isOpen && isFullscreen && (
        <div
          className="botilia-widget-root fixed inset-0 z-[10000] animate-fade-in flex flex-col"
          data-bs-nofade={noFade ? '' : undefined}
          style={FULLSCREEN_STYLE}
        >
          {/* Sticky header */}
          <header className="flex-shrink-0 bg-white z-20" style={HEADER_STYLE}>
            <div className="mx-auto px-4 sm:px-6 lg:px-10 py-3 sm:py-4" style={{ margin: '0 auto', padding: isMobile ? '12px 16px' : '16px 24px' }}>
              <div className="flex items-center gap-3" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div className="flex-1 relative" style={{ flex: 1, position: 'relative' }}>
                  <Search className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 h-5 w-5" style={{ color: 'rgb(var(--bs-brand, 237 20 91))', position: 'absolute', left: isMobile ? '12px' : '16px', top: '50%', transform: 'translateY(-50%)', width: '20px', height: '20px' }} />
                  <input
                    ref={modalInputRef}
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyDown={(e) => { if (isListening) stopListening(); handleKeyDown(e); }}
                    placeholder={isListening ? t('search.listening') : t('search.placeholder.short')}
                    className="w-full pl-10 sm:pl-12 pr-16 sm:pr-20 py-3 sm:py-3.5 text-base sm:text-lg bg-white border border-[#1a1a1a] rounded-[2px] focus:outline focus:outline-2 focus:outline-brand placeholder:text-gray-400"
                    style={{
                      ...INPUT_STYLE,
                      fontSize: isMobile ? '16px' : '18px',
                      paddingLeft: isMobile ? '36px' : '48px',
                      paddingRight: isMobile ? '64px' : '80px',
                      paddingTop: isMobile ? '10px' : '14px',
                      paddingBottom: isMobile ? '10px' : '14px',
                    }}
                    autoFocus
                    autoCorrect="off"
                    autoCapitalize="none"
                    spellCheck={false}
                  />
                  <div className="absolute right-3 sm:right-4 top-1/2 -translate-y-1/2 flex items-center gap-1" style={{ position: 'absolute', right: isMobile ? '8px' : '16px', top: '50%', transform: 'translateY(-50%)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    {query && (
                      <button type="button" onClick={clearQuery} className="p-2 rounded-full" style={{ color: 'rgb(var(--bs-brand, 237 20 91))', background: 'none', border: 'none', cursor: 'pointer', minWidth: '44px', minHeight: '44px', display: 'flex', alignItems: 'center', justifyContent: 'center' }} aria-label={t('search.clear')}>
                        <X className="h-5 w-5" />
                      </button>
                    )}
                    {isSupported ? (
                      <button
                        type="button"
                        onClick={isListening ? stopListening : startListening}
                        className={`voice-btn p-2 rounded-full transition-all ${isListening ? 'voice-listening' : 'hover:bg-gray-100'}`}
                        style={{ background: isListening ? 'rgba(239,68,68,0.1)' : 'none', border: 'none', cursor: 'pointer', minWidth: '44px', minHeight: '44px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        aria-label={isListening ? t('search.stopListening') : t('search.startListening')}
                      >
                        <Mic className="h-5 w-5" style={{ color: isListening ? '#ef4444' : '#9ca3af' }} />
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="voice-btn voice-disabled p-2 rounded-full"
                        style={{ background: 'none', border: 'none', cursor: 'not-allowed', opacity: 0.4, minWidth: '44px', minHeight: '44px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        title={t('search.voiceNotSupported')}
                        disabled
                      >
                        <MicOff className="h-5 w-5" style={{ color: '#9ca3af' }} />
                      </button>
                    )}
                  </div>
                </div>
                {langSwitcher && (
                  <div className="flex-shrink-0 inline-flex items-center gap-0.5 bg-gray-50 rounded-full border border-gray-200 p-0.5" style={{ flexShrink: 0 }}>
                    <button
                      type="button"
                      onClick={() => langSwitcher.setLang('el')}
                      className={`px-2.5 py-1 text-xs font-medium rounded-full transition-colors ${
                        langSwitcher.lang === 'el' ? 'bg-brand text-white' : 'text-gray-500 hover:text-gray-700'
                      }`}
                      aria-label="Ελληνικά"
                    >
                      EL
                    </button>
                    <button
                      type="button"
                      onClick={() => langSwitcher.setLang('en')}
                      className={`px-2.5 py-1 text-xs font-medium rounded-full transition-colors ${
                        langSwitcher.lang === 'en' ? 'bg-brand text-white' : 'text-gray-500 hover:text-gray-700'
                      }`}
                      aria-label="English"
                    >
                      EN
                    </button>
                  </div>
                )}
                <button type="button" onClick={closeWidget} className="flex-shrink-0 p-3 text-gray-500 hover:text-brand hover:bg-gray-100 rounded-[2px]" style={{ flexShrink: 0, padding: '12px', background: 'none', border: 'none', cursor: 'pointer', borderRadius: '2px', color: '#6b7280' }} aria-label={t('search.close')}>
                  <X className="h-6 w-6" style={{ width: '24px', height: '24px' }} />
                </button>
              </div>
              {voiceError && (
                <div className="mt-2 text-sm text-red-500 animate-fade-in" style={{ marginTop: '8px', fontSize: '14px', color: '#ef4444' }}>
                  {voiceError}
                </div>
              )}
              {headerContent}
            </div>
          </header>

          {/* Scrollable body */}
          <main ref={scrollContainerRef} className="flex-1 overflow-y-auto botilia-scrollbar" style={MAIN_SCROLL_STYLE}>
            {showDiscoveryView && (
              <div className="mx-auto px-3 sm:px-6 lg:px-10 py-4 sm:py-8 animate-fade-in">
                <LayoutRenderer zone="columns" layoutConfig={layoutConfig} {...widgetBaseProps} />
                {isLoadingDefaults && (
                  <div className="flex items-center justify-center py-12">
                    <div className="flex gap-1.5">
                      <div className="w-2.5 h-2.5 rounded-full bg-brand/40 animate-pulse" />
                      <div className="w-2.5 h-2.5 rounded-full bg-brand/40 animate-pulse" style={{ animationDelay: '150ms' }} />
                      <div className="w-2.5 h-2.5 rounded-full bg-brand/40 animate-pulse" style={{ animationDelay: '300ms' }} />
                    </div>
                  </div>
                )}
              </div>
            )}
            {showSearchResults && renderSearchResults()}
          </main>
        </div>
      )}

      {/* ---------------------------------------------------------------- */}
      {/* Dropdown panel                                                    */}
      {/* ---------------------------------------------------------------- */}
      {isOpen && !isFullscreen && (
        <div
          data-search-panel
          className="botilia-widget-root fixed z-[10000] bg-white rounded-[2px] shadow-[0_3px_16px_rgba(0,0,0,0.17)] overflow-hidden animate-fade-in flex flex-col"
          data-bs-nofade={noFade ? '' : undefined}
          style={{ ...dropdownStyle, ...WIDGET_ROOT_STYLE, position: 'fixed', zIndex: 10000 }}
        >
          {/* Panel header */}
          <div className="bg-white border-b border-gray-100 px-4 py-3 flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4" style={{ color: 'rgb(var(--bs-brand, 237 20 91))' }} />
                <input
                  ref={modalInputRef}
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={(e) => { if (isListening) stopListening(); handleKeyDown(e); }}
                  placeholder={isListening ? t('search.listening') : t('search.placeholder.short')}
                  className="w-full pl-10 pr-16 py-2.5 text-base bg-white border border-[#1a1a1a] rounded-[2px] focus:outline focus:outline-2 focus:outline-brand placeholder:text-gray-400"
                  autoFocus
                  autoCorrect="off"
                  autoCapitalize="none"
                  spellCheck={false}
                />
                <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1">
                  {query && (
                    <button type="button" onClick={clearQuery} className="p-0.5 rounded-full" style={{ color: 'rgb(var(--bs-brand, 237 20 91))', background: 'none', border: 'none', cursor: 'pointer' }} aria-label={t('search.clear')}>
                      <X className="h-4 w-4" />
                    </button>
                  )}
                  {isSupported ? (
                    <button
                      type="button"
                      onClick={isListening ? stopListening : startListening}
                      className={`voice-btn p-1 rounded-full transition-all ${isListening ? 'voice-listening' : 'hover:bg-gray-100'}`}
                      style={{ background: isListening ? 'rgba(239,68,68,0.1)' : 'none', border: 'none', cursor: 'pointer', minWidth: '28px', minHeight: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                      aria-label={isListening ? t('search.stopListening') : t('search.startListening')}
                    >
                      <Mic className="h-4 w-4" style={{ color: isListening ? '#ef4444' : '#9ca3af' }} />
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="voice-btn voice-disabled p-1 rounded-full"
                      style={{ background: 'none', border: 'none', cursor: 'not-allowed', opacity: 0.4, minWidth: '28px', minHeight: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                      title={t('search.voiceNotSupported')}
                      disabled
                    >
                      <MicOff className="h-4 w-4" style={{ color: '#9ca3af' }} />
                    </button>
                  )}
                </div>
              </div>
              {langSwitcher && (
                <div className="inline-flex items-center gap-0.5 bg-gray-50 rounded-full border border-gray-200 p-0.5">
                  <button
                    type="button"
                    onClick={() => langSwitcher.setLang('el')}
                    className={`px-2 py-0.5 text-[10px] font-medium rounded-full transition-colors ${
                      langSwitcher.lang === 'el' ? 'bg-brand text-white' : 'text-gray-500 hover:text-gray-700'
                    }`}
                    aria-label="Ελληνικά"
                  >
                    EL
                  </button>
                  <button
                    type="button"
                    onClick={() => langSwitcher.setLang('en')}
                    className={`px-2 py-0.5 text-[10px] font-medium rounded-full transition-colors ${
                      langSwitcher.lang === 'en' ? 'bg-brand text-white' : 'text-gray-500 hover:text-gray-700'
                    }`}
                    aria-label="English"
                  >
                    EN
                  </button>
                </div>
              )}
              <button type="button" onClick={closeWidget} className="p-2 text-gray-400 hover:text-brand rounded-[2px] transition-colors" aria-label={t('search.close')}>
                <X className="h-5 w-5" />
              </button>
            </div>
            {voiceError && (
              <div className="mt-2 text-sm text-red-500 animate-fade-in">
                {voiceError}
              </div>
            )}
            {headerContent}
          </div>

          {/* Panel body */}
          <div ref={scrollContainerRef} className="overflow-y-auto flex-1 botilia-scrollbar">
            {showDiscoveryView && (
              <div className="px-4 py-4 animate-fade-in">
                <LayoutRenderer zone="columns" layoutConfig={layoutConfig} {...widgetBaseProps} />
              </div>
            )}
            {showSearchResults && (
              <div className="px-4 py-4">
                {renderSearchResults()}
              </div>
            )}
          </div>
        </div>
      )}
    </FontSizeProvider>
  );

  // -------------------------------------------------------------------------
  // Search results (shared between fullscreen + dropdown)
  // -------------------------------------------------------------------------

  function renderSearchResults() {
    // Changing a filter keeps the page on screen, dimmed, until the new results
    // land. Swapping it for the skeleton unmounted the filter sidebar, which took
    // the focus out of the price fields and closed the mobile filter drawer.
    const keepPreviousResults = isLoading && !!results && results.query.trim() === query.trim();
    const noProducts = !!results && results.products.length === 0 && !isLoading;

    return (
      <div className={`mx-auto px-3 sm:px-6 py-4 sm:py-6 ${isFullscreen ? 'lg:px-10' : 'max-w-6xl'}`}>
        {isLoading && !keepPreviousResults && (
          <div className="animate-fade-in">
            <div className="mb-4 flex items-center justify-center">
              <span className="text-sm text-gray-400 animate-pulse">{t('search.searching', { query })}</span>
            </div>
            <SkeletonGrid count={8} variant="product" />
          </div>
        )}

        {results && (!isLoading || keepPreviousResults) && (
          <div className={keepPreviousResults ? '' : 'animate-fade-in'}>
            {/* Filters bar */}
            <div className="flex flex-wrap items-center justify-between gap-2 sm:gap-4 mb-4 sm:mb-6 pb-3 sm:pb-4 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <span className="text-sm text-gray-500">
                  {query.trim() ? t('search.results', { count: results.total }) : t('search.products', { count: results.total })}
                </span>
                {filterChips.length > 0 && (
                  <FilterChips chips={filterChips} onRemove={handleRemoveChip} onClearAll={clearFilters} />
                )}
              </div>
              <div className="flex flex-wrap items-center justify-end gap-2 sm:gap-3 ml-auto">
                <SortDropdown value={filters.sort ?? 'relevance'} onChange={handleSortChange} />
                {isCompact && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setShowMobileFilters(true); }}
                    className="flex items-center gap-2 px-3.5 py-2.5 bg-brand text-white rounded-full shadow-md active:scale-95 transition-transform"
                    aria-label={t('filter.label')}
                  >
                    <SlidersHorizontal className="h-[18px] w-[18px]" />
                    <span className="text-[13px] font-medium">{t('filter.label')}</span>
                    {filterChips.length > 0 && (
                      <span className="flex items-center justify-center w-[18px] h-[18px] bg-white text-brand text-xs font-bold rounded-full">
                        {filterChips.length}
                      </span>
                    )}
                  </button>
                )}
              </div>
            </div>

            {results.correctedQuery && (
              <div className="mb-4 text-sm text-[#020202]">
                {t('search.showingResultsFor')}{' '}
                <strong className="font-semibold text-brand-ink break-words">{results.correctedQuery}</strong>
                <div className="mt-0.5 text-xs text-gray-500">
                  {t('search.searchInsteadFor')}{' '}
                  <button type="button" onClick={searchExact} className="underline hover:no-underline break-words">
                    «{query.trim()}»
                  </button>
                </div>
              </div>
            )}

            {noProducts && (
              <p className="mb-1 text-base sm:text-lg text-[#020202]">
                {query.trim() ? t('noResults.notFound', { query: query.trim() }) : t('noResults.title')}
              </p>
            )}

            {results.didYouMean && (
              <p className="mb-1 text-sm text-[#020202]">
                {t('search.didYouMean')}{' '}
                <button type="button" onClick={() => selectSuggestion(results.didYouMean!)} className="font-semibold underline hover:no-underline text-brand-ink break-words">
                  {results.didYouMean}
                </button>
                {t('search.didYouMeanMark')}
              </p>
            )}

            {noProducts && (
              <p className="mb-6 text-sm text-gray-500">
                {query.trim() ? t('noResults.tryBestSellers') : t('noResults.tryDifferent')}
              </p>
            )}

            <div className="flex gap-4 sm:gap-6">
              {/* Desktop sidebar — rendered only on wide layouts (conditional
                  render, not CSS hide, so host CSS can't force it visible on mobile) */}
              {/* With nothing found and nothing filtered, the sidebar has nothing to offer. */}
              {!isCompact && !(noProducts && !hasActiveFilters) && (
                <div
                  className="flex-shrink-0"
                  // overflowAnchor: the sidebar precedes the grid in DOM order and
                  // spans the scrollport, so Chrome picks its scroll anchor here.
                  // When the catalog facets land after the first paint the rows
                  // above the viewport change and Chrome "helpfully" shifts
                  // <main>'s scrollTop to keep the sidebar still — moving the
                  // results the user is actually reading (and undoing a restored
                  // scroll position). Anchor to the results instead.
                  style={{ width: `calc(${layoutConfig.filterColumnWidthPercent ?? 20}% - 12px)`, overflowAnchor: 'none' }}
                >
                  <FacetSidebar facets={results.facets} filters={filters} onChangeFilters={setFilters} isVisible={true} onToggleVisibility={toggleSidebar} />
                </div>
              )}

              {/* Mobile fullscreen filter overlay — portaled out of the
                  scrolling <main> so it covers the results on iOS instead of
                  being painted behind the product images (see WidgetPortal). */}
              {isCompact && showMobileFilters && (
                <WidgetPortal>
                  <div className="fixed inset-0 z-[10003] bg-white flex flex-col animate-fade-in">
                    <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
                      <h2 className="text-lg font-semibold">{t('filter.label')}</h2>
                      <button
                        type="button"
                        onClick={() => setShowMobileFilters(false)}
                        className="p-2 -mr-2 text-gray-500 hover:text-brand rounded-[2px] transition-colors"
                      >
                        <X className="h-5 w-5" />
                      </button>
                    </div>
                    <div className="flex-1 overflow-y-auto px-2 py-2">
                      <FacetSidebar facets={results.facets} filters={filters} onChangeFilters={setFilters} isVisible={true} onToggleVisibility={toggleSidebar} />
                    </div>
                    <div className="flex-shrink-0 px-5 py-3 border-t border-gray-200 flex gap-3">
                      <button
                        type="button"
                        onClick={() => { clearFilters(); setShowMobileFilters(false); }}
                        className="flex-1 px-4 py-3 text-sm font-medium text-gray-700 bg-gray-100 rounded-[2px] hover:bg-gray-200 transition-colors"
                      >
                        {t('filter.clearAll')}
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowMobileFilters(false)}
                        className="botilia-button flex-1 px-4 py-3 text-sm font-medium"
                      >
                        {results ? t('search.results', { count: results.total }) : t('filter.label')}
                      </button>
                    </div>
                  </div>
                </WidgetPortal>
              )}

              <div className={`flex-1 min-w-0 transition-opacity ${keepPreviousResults ? 'opacity-50 pointer-events-none' : ''}`}>
                {noProducts && query.trim() && (
                  <BestSellerWinesWidget
                    config={{ id: 'no-results-best-sellers', type: 'best-seller-wines', visible: true, maxItems: 8 }}
                    defaults={defaults}
                    results={null}
                    recentSearches={[]}
                    onSearch={setQuery}
                    onClearRecentSearches={clearRecentSearches}
                    onProductClick={trackProductClick}
                    itemsPerRow={activeItemsPerRow}
                  />
                )}
                <ResultsRenderer
                  resultsLayout={layoutConfig.resultsLayout}
                  resultsColumnCount={layoutConfig.resultsColumnCount}
                  resultsHeaderSections={layoutConfig.resultsHeaderSections}
                  resultsColumns={layoutConfig.resultsColumns}
                  autoCollapseEmptyColumns={layoutConfig.autoCollapseEmptyColumns}
                  results={results}
                  itemsPerRow={activeItemsPerRow}
                  hasMore={hasMore}
                  isLoadingMore={isLoadingMore}
                  isAIThinking={isAIThinking}
                  onProductClick={trackProductClick}
                  onLoadMore={loadMore}
                />

                {results.products.length > 0 && (
                  <div className="text-center py-4 text-xs text-gray-400">
                    {t('search.resultsTime', { count: results.total, time: results.took })}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {error && (
          <div className="text-center py-8 sm:py-16 animate-fade-in">
            <p className="text-red-600 mb-2">{t('search.error')}</p>
            <p className="text-sm text-gray-500">{error}</p>
          </div>
        )}
      </div>
    );
  }
};
